import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@nxsys/store';
import { socketConversation } from '@nxsys/services/sockets';
import { messageActions } from '@nxsys/store/slices/message/reducer/messageSlice';
import { ChatMessage, Conversation } from '@nxsys/store/slices/message/types';

type UseConversationSocketOptions = {
  userId?: string;
  agencyId?: string;
  companyParentId?: string;
  isAdmin?: boolean;
};

export const useConversationSocket = ({
  userId,
  agencyId,
  companyParentId,
  isAdmin,
}: UseConversationSocketOptions): void => {
  const dispatch = useDispatch<AppDispatch>();
  // Track processed message IDs to deduplicate across channels
  const processedMessageIds = useRef<Set<number>>(new Set());
  const processedConversationIds = useRef<Set<number>>(new Set());

  const handleNewConversation = useCallback(
    (eventData: { data: Conversation; success: boolean }) => {
      if (!eventData?.success || !eventData?.data) return;
      const conv = eventData.data;
      if (processedConversationIds.current.has(conv.id)) return;
      processedConversationIds.current.add(conv.id);
      // Clean up old IDs to prevent memory leak
      if (processedConversationIds.current.size > 200) {
        const ids = Array.from(processedConversationIds.current);
        processedConversationIds.current = new Set(ids.slice(-100));
      }
      dispatch(messageActions.onNewConversation(conv));
      // Fetch global unread count
      dispatch(messageActions.fetchUnreadCount());
    },
    [dispatch],
  );

  const handleNewMessage = useCallback(
    (eventData: {
      data: ChatMessage;
      unreadCount?: number;
      success: boolean;
    }) => {
      if (!eventData?.success || !eventData?.data) return;
      const message = eventData.data;
      if (processedMessageIds.current.has(message.id)) return;
      processedMessageIds.current.add(message.id);
      // Clean up old IDs
      if (processedMessageIds.current.size > 500) {
        const ids = Array.from(processedMessageIds.current);
        processedMessageIds.current = new Set(ids.slice(-250));
      }
      dispatch(
        messageActions.onNewMessage({
          message,
          unreadCount: eventData.unreadCount,
          currentUserId: userId,
        }),
      );
      // Always fetch global unread count
      dispatch(messageActions.fetchUnreadCount());
    },
    [dispatch, userId],
  );

  const handleConversationRead = useCallback(
    (eventData: { data: Conversation; success: boolean }) => {
      if (!eventData?.success || !eventData?.data) return;
      dispatch(messageActions.onConversationRead(eventData.data));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!userId) return;

    const events: [string, (...args: any[]) => void][] = [];

    // User-level events (always)
    events.push([`new-conversation-${userId}`, handleNewConversation]);
    events.push([`new-message-${userId}`, handleNewMessage]);
    events.push([`conversation-read-${userId}`, handleConversationRead]);

    // Agency-level events
    if (agencyId) {
      events.push([
        `new-conversation-agency-${agencyId}`,
        handleNewConversation,
      ]);
      events.push([`new-message-agency-${agencyId}`, handleNewMessage]);
      events.push([
        `conversation-read-agency-${agencyId}`,
        handleConversationRead,
      ]);
    }

    // Broadcast events (admin only)
    if (isAdmin && companyParentId) {
      events.push([
        `new-conversation-broadcast-${companyParentId}`,
        handleNewConversation,
      ]);
      events.push([
        `new-message-broadcast-${companyParentId}`,
        handleNewMessage,
      ]);
      events.push([
        `conversation-read-broadcast-${companyParentId}`,
        handleConversationRead,
      ]);
    }

    // Register all listeners
    for (const [event, handler] of events) {
      socketConversation.on(event, handler);
    }

    return () => {
      for (const [event, handler] of events) {
        socketConversation.off(event, handler);
      }
    };
  }, [
    userId,
    agencyId,
    companyParentId,
    isAdmin,
    handleNewConversation,
    handleNewMessage,
    handleConversationRead,
  ]);
};

export default useConversationSocket;
